import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { HardDrive, Loader2 } from 'lucide-react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { usePermission } from '../components';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import limitsApi from '../quotas/limitsApi';

const GIB = 1024 ** 3;
const allowanceText = (bytes) => bytes < 0 ? 'Unlimited' : `${(bytes / GIB).toLocaleString()} GB`;
const usageText = (bytes) => bytes >= GIB
  ? `${(bytes / GIB).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`
  : `${(bytes / (1024 ** 2)).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;

export default function DocFlowStorageExtraCard({ tenantId, onSaved }) {
  const { getAdminToken } = useAdminAuth();
  const read = usePermission('quota:read');
  const write = usePermission('quota:write');
  const [quota, setQuota] = useState(null);
  const [extra, setExtra] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await limitsApi.getTenantLimits(getAdminToken(), tenantId);
      const row = (payload.items || []).find((item) => item.key === 'storage.bytes_total');
      if (!row || !Number.isInteger((row.docflow_storage_extra_bytes || 0) / GIB)) {
        throw new Error('The File Manager allowance is unavailable.');
      }
      setQuota(row);
      setExtra(String((row.docflow_storage_extra_bytes || 0) / GIB));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Storage allowance could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [getAdminToken, tenantId]);

  useEffect(() => {
    if (read.allowed) load();
    else setLoading(false);
  }, [read.allowed, load]);

  const valid = /^\d+$/.test(extra) && Number(extra) <= 931322;
  const currentExtra = quota ? (quota.docflow_storage_extra_bytes || 0) / GIB : null;
  const otherAddOns = quota ? Math.max(0, (quota.add_on_allowance || 0) - (quota.docflow_storage_extra_bytes || 0)) / GIB : 0;
  const changed = valid && Number(extra) !== currentExtra;
  const save = async () => {
    if (!changed || saving) return;
    setSaving(true);
    try {
      await limitsApi.setDocFlowStorageExtra(getAdminToken(), tenantId, {
        extra_gb: Number(extra), expected_extra_gb: currentExtra,
      });
      await load();
      onSaved?.();
      toast.success('File Manager allowance updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Storage allowance could not be updated. Refresh and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-muted/30 p-4" data-testid="docflow-storage-editor">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-primary/10 p-2 text-primary"><HardDrive className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">DocFlow storage</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Extra GB is added to the tenant's File Manager allowance. All files share this space.
          </p>
        </div>
      </div>
      {loading ? <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Loading allowance…</p> : null}
      {!read.allowed ? <p className="mt-3 text-xs text-muted-foreground">Quota read permission is required.</p> : null}
      {error ? <p role="alert" className="mt-3 text-xs text-destructive">{String(error)}</p> : null}
      {quota && read.allowed && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <div><span className="block text-muted-foreground">Template base</span><strong>{allowanceText(quota.template_limit)}</strong></div>
            <div><span className="block text-muted-foreground">Extra</span><strong>{currentExtra.toLocaleString()} GB</strong></div>
            <div><span className="block text-muted-foreground">Effective total</span><strong>{allowanceText(quota.effective_limit)}</strong></div>
            <div data-testid="docflow-storage-usage">
              <span className="block text-muted-foreground">File Manager recorded</span>
              <strong>{quota.measurement_status === 'measured' && Number.isFinite(quota.current_usage)
                ? usageText(quota.current_usage) : 'Unavailable'}</strong>
            </div>
          </div>
          {quota.runtime_status === 'declared_only' && (
            <p className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs leading-5 text-warning-text" data-testid="docflow-storage-enforcement-note">
              This allowance is currently informational. File uploads are not blocked when it is reached.
            </p>
          )}
          <p className="text-xs leading-5 text-muted-foreground">
            Recorded usage excludes legacy DocFlow copies until storage reconciliation is complete.
          </p>
          {otherAddOns > 0 && <p className="text-xs text-muted-foreground">Includes {otherAddOns.toLocaleString()} GB from other storage add-ons.</p>}
          {quota.override != null && <p className="text-xs text-warning-text">A tenant limit override currently sets the effective total. Remove it in Limits to use the template plus extra GB.</p>}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="flex-1 text-xs font-medium text-foreground" htmlFor="edit-docflow-extra-gb">
              Extra storage (GB)
              <Input id="edit-docflow-extra-gb" type="number" min="0" max="931322" step="1" inputMode="numeric"
                     value={extra} onChange={(event) => setExtra(event.target.value)} className="mt-1"
                     disabled={!write.allowed || saving} data-testid="edit-docflow-extra-storage-input" />
            </label>
            <Button type="button" onClick={save} disabled={!write.allowed || !changed || saving} className="sm:shrink-0">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save extra GB
            </Button>
          </div>
          {!valid && <p role="alert" className="text-xs text-destructive">Enter a non-negative whole GB amount.</p>}
          {!write.allowed && <p className="text-xs text-muted-foreground">Quota write permission is required to change this allowance.</p>}
        </div>
      )}
    </section>
  );
}
